import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';
import { App as NativeApp } from '@capacitor/app';
import { isNative } from '../services/platform';
import { DISCLAIMER_SHORT } from '../config/legal';
import { useEntitlement, OPEN_SIGN_IN, takeRememberedPaywall } from '../context/EntitlementContext';
import { useFinancialPlan } from '../context/FinancialPlanContext';

// Matches the store versionName / MARKETING_VERSION; package.json still says 0.3.0.
const APP_VERSION = '1.0';

const navLinks = [
  { to: '/', label: 'Home', key: '1' },
  { to: '/real-estate', label: 'Real Estate', key: '2' },
  { to: '/financial-planning', label: 'Financial Planning', key: '3' },
];

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  // Any screen can ask for the sign-in window (the paywall does, before checkout).
  useEffect(() => {
    const open = (event) => {
      setAuthMode(event?.detail?.mode === 'register' ? 'register' : 'login');
      setAuthModalOpen(true);
    };
    window.addEventListener(OPEN_SIGN_IN, open);
    return () => window.removeEventListener(OPEN_SIGN_IN, open);
  }, []);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileName, setProfileName] = useState({ firstName: '', lastName: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [accountDeleted, setAccountDeleted] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const {
    user, isAuthenticated, logout, updateProfile, deleteAccount, updatePassword,
    passwordRecovery, cancelPasswordRecovery, loading: authLoading,
  } = useAuth();
  const { sync } = useFinancialPlan();
  const syncLabel = {
    loading: 'Cloud sync: loading your plan...',
    pending: 'Cloud sync: saving...',
    saved: 'Cloud sync: saved',
    error: 'Cloud sync: not saved. Tap to retry',
  }[sync.state];

  const { isPro, openPaywall, paywallFeature, closePaywall } = useEntitlement();
  // Back from creating an account or signing in on the way to buying: show the offer again.
  useEffect(() => {
    if (!isAuthenticated) return;
    const feature = takeRememberedPaywall();
    if (feature) openPaywall(feature);
  }, [isAuthenticated, openPaywall]);

  const backState = useRef({});
  backState.current = { authModalOpen, userMenuOpen, profileOpen, deleteOpen, mobileMenuOpen, paywallFeature, pathname: location.pathname };
  useEffect(() => {
    if (!isNative) return undefined;
    const listener = NativeApp.addListener('backButton', () => {
      const open = backState.current;
      if (open.paywallFeature) closePaywall();
      else if (open.deleteOpen) setDeleteOpen(false);
      else if (open.profileOpen) setProfileOpen(false);
      else if (open.authModalOpen) setAuthModalOpen(false);
      else if (open.userMenuOpen) setUserMenuOpen(false);
      else if (open.mobileMenuOpen) setMobileMenuOpen(false);
      else if (open.pathname !== '/') navigate(-1);
      else NativeApp.exitApp();
    });
    return () => { listener.then((handle) => handle.remove()); };
  }, [navigate, closePaywall]);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDialogError('');
    try {
      await deleteAccount();
      setDeleteOpen(false);
      setDeleteConfirm('');
      setAccountDeleted(true);
      navigate('/');
    } catch (err) {
      setDialogError(err.message);
    }
    setDeleting(false);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setDialogError('Password must be at least 8 characters.');
      return;
    }
    setSavingPassword(true);
    setDialogError('');
    try {
      await updatePassword(newPassword);
      setNewPassword('');
    } catch (err) {
      setDialogError(err.message || 'Could not update your password.');
    }
    setSavingPassword(false);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (!e.altKey) return;
      const link = navLinks.find(l => l.key === e.key);
      if (link) {
        e.preventDefault();
        navigate(link.to);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-terminal-bg terminal-scrollbar">
      <nav className="bg-surface-primary border-b border-surface-border safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-14">
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-2 group min-h-[44px]">
                <img
                  src={`${process.env.PUBLIC_URL}/ultronic-nav.png`}
                  alt=""
                  className="w-8 h-8 rounded object-cover object-[center_30%]"
                />
                <span className="text-terminal-green font-display text-lg font-bold tracking-wide crt-glow">
                  ULTRONIC
                </span>
                <span className="text-txt-secondary font-display text-xs font-medium uppercase tracking-widest hidden sm:inline">
                  Terminal
                </span>
              </Link>

              <div className="hidden sm:flex sm:ml-8 sm:space-x-1">
                {navLinks.map((link) => {
                  const isActive = link.to === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(link.to);
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`
                        inline-flex items-center px-3 py-2 text-xs font-mono uppercase tracking-wider
                        border-b-2 transition-colors duration-200
                        ${isActive
                          ? 'border-terminal-green text-terminal-green'
                          : 'border-transparent text-txt-secondary hover:text-terminal-dim-green hover:border-terminal-dark-green'
                        }
                      `}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-txt-muted text-xs font-mono hidden sm:inline">v{APP_VERSION}</span>

              {!authLoading && (
                isAuthenticated ? (
                  <div className="relative">
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      aria-label="Account menu"
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-elevated border border-surface-border hover:border-terminal-dark-green transition-colors"
                    >
                      {user?.avatarUrl ? (
                        <img src={user.avatarUrl} alt="" className="w-6 h-6 rounded-full" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-terminal-dark-green flex items-center justify-center">
                          <span className="text-terminal-green text-xs font-bold font-mono">
                            {user?.firstName?.[0]?.toUpperCase() || 'U'}
                          </span>
                        </div>
                      )}
                      <span className="text-txt-primary text-xs font-mono hidden sm:inline">
                        {user?.firstName || 'User'}
                      </span>
                    </button>

                    {userMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                        <div className="absolute right-0 top-full mt-2 w-64 bg-surface-elevated border border-surface-border rounded-lg shadow-glow-green-sm z-50 py-1">
                          <div className="px-4 py-2 border-b border-surface-border">
                            <p className="text-xs font-mono text-terminal-green truncate">{user?.email}</p>
                            {(user?.firstName || user?.lastName) && (
                              <p className="text-xs font-mono text-txt-secondary mt-0.5">
                                {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                              </p>
                            )}
                          </div>
                          {isPro && syncLabel && (
                            <button
                              onClick={sync.state === 'error' ? sync.retry : undefined}
                              disabled={sync.state !== 'error'}
                              className={`w-full text-left px-4 py-3 text-xs font-mono ${sync.state === 'error' ? 'text-terminal-amber hover:bg-surface-overlay' : 'text-txt-secondary'}`}
                            >
                              {syncLabel}
                            </button>
                          )}
                          {!isPro && (
                            <button
                              onClick={() => {
                                setUserMenuOpen(false);
                                openPaywall('cloudSync');
                              }}
                              className="w-full text-left px-4 py-3 text-xs font-mono text-terminal-amber hover:bg-surface-overlay transition-colors"
                            >
                              Cloud sync needs Pro
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              setProfileName({ firstName: user?.firstName || '', lastName: user?.lastName || '' });
                              setProfileOpen(true);
                            }}
                            className="w-full text-left px-4 py-3 text-xs font-mono text-txt-secondary hover:text-terminal-cyan hover:bg-surface-overlay transition-colors"
                          >
                            Edit Profile
                          </button>
                          <button
                            onClick={async () => {
                              setUserMenuOpen(false);
                              await logout();
                            }}
                            className="w-full text-left px-4 py-3 text-xs font-mono text-txt-secondary hover:text-terminal-red hover:bg-surface-overlay transition-colors"
                          >
                            Sign Out
                          </button>
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              setDialogError('');
                              setDeleteConfirm('');
                              setDeleteOpen(true);
                            }}
                            className="w-full text-left px-4 py-3 text-xs font-mono text-terminal-red hover:bg-surface-overlay transition-colors border-t border-surface-border"
                          >
                            Delete Account
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => { setAuthMode('login'); setAuthModalOpen(true); }}
                    className="glow-btn glow-btn-green px-3 rounded-lg text-xs font-mono uppercase tracking-wider"
                  >
                    Sign In
                  </button>
                )
              )}

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="sm:hidden text-txt-secondary hover:text-terminal-green p-2.5 -mr-2.5"
                aria-label="Toggle menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen
                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  }
                </svg>
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-surface-border bg-surface-elevated">
            <div className="px-4 py-2 space-y-1">
              {navLinks.map((link) => {
                const isActive = link.to === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(link.to);
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-3 py-3 text-xs font-mono uppercase tracking-wider rounded ${
                      isActive
                        ? 'text-terminal-green bg-terminal-dark-green/20'
                        : 'text-txt-secondary hover:text-terminal-dim-green hover:bg-surface-overlay'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {accountDeleted && (
        <div className="bg-surface-elevated border-b border-terminal-green/40 px-4 py-3 flex flex-wrap items-center justify-between gap-3" role="status">
          <p className="text-txt-primary text-sm">
            Your account and the plan data synced to it have been deleted. Any subscription bought on this website was cancelled
            and your payment details were removed. Nothing more will be charged.
          </p>
          <button onClick={() => setAccountDeleted(false)} className="min-h-[44px] px-3 text-terminal-cyan text-xs font-mono uppercase tracking-wider hover:underline">Dismiss</button>
        </div>
      )}
      {sync.backup && (
        <div className="bg-surface-elevated border-b border-terminal-amber-dim px-4 py-3" role="status">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-x-4 gap-y-2">
            <p className="text-terminal-amber text-xs font-mono flex-1 min-w-[220px]">
              Your account's saved plan was loaded. The version that was on this device was kept
              {sync.backupCount > 1 ? ` (${sync.backupCount} kept)` : ''}.
            </p>
            <button onClick={sync.restoreBackup} className="text-terminal-cyan text-xs font-mono uppercase tracking-wider hover:underline py-2">
              Use the device plan instead
            </button>
            <button onClick={sync.discardBackup} className="text-txt-secondary text-xs font-mono uppercase tracking-wider hover:underline py-2">
              Discard it
            </button>
          </div>
        </div>
      )}
      {sync.state === 'error' && (
        <div className="bg-surface-elevated border-b border-terminal-amber-dim px-4 py-2" role="status">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-x-4">
            <p className="text-terminal-amber text-xs font-mono flex-1 min-w-[220px]">
              Couldn't reach the server. Your plan is saved on this device and will sync when you're back online.
            </p>
            <button onClick={sync.retry} className="text-terminal-cyan text-xs font-mono uppercase tracking-wider hover:underline py-2">
              Retry now
            </button>
          </div>
        </div>
      )}
      {sync.localSaveFailed && (
        <div className="bg-surface-elevated border-b border-terminal-red-dim px-4 py-2" role="alert">
          <p className="max-w-7xl mx-auto text-terminal-red text-xs font-mono">
            This device's storage is full or blocked, so changes aren't being saved. Free up space or leave private browsing.
          </p>
        </div>
      )}

      <main className="flex-grow pb-20 sm:pb-0">
        {children}
      </main>

      <AuthModal isOpen={authModalOpen} initialMode={authMode} onClose={() => setAuthModalOpen(false)} />

      {profileOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" role="dialog" aria-modal="true" aria-label="Edit profile">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setProfileOpen(false)} />
          <div className="relative w-full max-w-sm mx-4 terminal-card p-0 overflow-hidden">
            <div className="bg-surface-elevated border-b border-surface-border px-6 py-4 flex items-center justify-between">
              <h2 className="font-display font-bold text-terminal-green crt-glow uppercase tracking-wider text-lg">
                Profile
              </h2>
              <button
                onClick={() => setProfileOpen(false)}
                aria-label="Close profile"
                className="text-txt-muted hover:text-terminal-green transition-colors p-3 -m-3"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="profile-firstName" className="block text-txt-secondary text-xs font-mono uppercase tracking-wide mb-1">First Name</label>
                <input
                  id="profile-firstName"
                  type="text"
                  autoComplete="given-name"
                  value={profileName.firstName}
                  onChange={(e) => setProfileName(p => ({ ...p, firstName: e.target.value }))}
                  className="terminal-input w-full"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label htmlFor="profile-lastName" className="block text-txt-secondary text-xs font-mono uppercase tracking-wide mb-1">Last Name</label>
                <input
                  id="profile-lastName"
                  type="text"
                  autoComplete="family-name"
                  value={profileName.lastName}
                  onChange={(e) => setProfileName(p => ({ ...p, lastName: e.target.value }))}
                  className="terminal-input w-full"
                  placeholder="Optional"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setProfileOpen(false)}
                  className="flex-1 bg-surface-elevated border border-surface-border py-2 rounded-lg text-xs font-mono uppercase text-txt-secondary hover:text-txt-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={profileSaving}
                  onClick={async () => {
                    setProfileSaving(true);
                    try {
                      await updateProfile(profileName);
                      setProfileOpen(false);
                    } catch { }
                    setProfileSaving(false);
                  }}
                  className="flex-1 glow-btn glow-btn-green py-2 rounded-lg text-xs font-mono uppercase disabled:opacity-40 transition-all"
                >
                  {profileSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteOpen && (
        <div className="fixed inset-0 z-[100] overflow-y-auto" role="dialog" aria-modal="true" aria-label="Delete account">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !deleting && setDeleteOpen(false)} />
          <div className="relative min-h-full flex items-center justify-center py-6">
            <div className="relative w-full max-w-sm mx-4 terminal-card p-6 space-y-4">
              <h2 className="font-display font-bold text-terminal-red uppercase tracking-wider text-lg">Delete Account</h2>
              <p className="text-sm text-txt-secondary">
                This permanently deletes your account ({user?.email}) and all plan data synced to it. It cannot be
                undone. A subscription bought on this website is cancelled and your payment details are removed
                from our payment processor; a one-time purchase is given up. A subscription bought through the App
                Store or Google Play is not cancelled by this: cancel it in your store account settings.
              </p>
              <div>
                <label htmlFor="delete-confirm" className="block text-txt-secondary text-xs font-mono uppercase tracking-wide mb-1">
                  Type DELETE to confirm
                </label>
                <input
                  id="delete-confirm"
                  type="text"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className="terminal-input w-full"
                  autoComplete="off"
                  autoCapitalize="characters"
                />
              </div>
              {dialogError && <p className="text-terminal-red text-sm font-mono" role="alert">{dialogError}</p>}
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteOpen(false)}
                  disabled={deleting}
                  className="flex-1 bg-surface-elevated border border-surface-border py-3 rounded-lg text-xs font-mono uppercase text-txt-secondary hover:text-txt-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting || deleteConfirm.trim().toUpperCase() !== 'DELETE'}
                  className="flex-1 glow-btn glow-btn-red rounded-lg text-xs font-mono uppercase disabled:opacity-40 transition-all"
                >
                  {deleting ? 'Deleting...' : 'Delete Forever'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {passwordRecovery && (
        <div className="fixed inset-0 z-[100] overflow-y-auto" role="dialog" aria-modal="true" aria-label="Set a new password">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative min-h-full flex items-center justify-center py-6">
            <form onSubmit={handleSetPassword} className="relative w-full max-w-sm mx-4 terminal-card p-6 space-y-4">
              <h2 className="font-display font-bold text-terminal-green crt-glow uppercase tracking-wider text-lg">Set a New Password</h2>
              <div>
                <label htmlFor="new-password" className="block text-txt-secondary text-xs font-mono uppercase tracking-wide mb-1">
                  New Password
                </label>
                <input
                  id="new-password"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="terminal-input w-full"
                  placeholder="Min 8 characters"
                  autoComplete="new-password"
                />
              </div>
              {dialogError && <p className="text-terminal-red text-sm font-mono" role="alert">{dialogError}</p>}
              <button
                type="submit"
                disabled={savingPassword}
                className="w-full glow-btn glow-btn-green rounded-lg font-mono uppercase tracking-wider text-sm disabled:opacity-40"
              >
                {savingPassword ? 'Saving...' : 'Save Password'}
              </button>
              <button
                type="button"
                onClick={() => { setDialogError(''); setNewPassword(''); cancelPasswordRecovery(); }}
                className="w-full py-2 text-xs font-mono uppercase tracking-wider text-txt-secondary hover:text-txt-primary"
              >
                Not now
              </button>
            </form>
          </div>
        </div>
      )}

      <footer className="bg-surface-primary border-t border-surface-border safe-bottom">
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8 space-y-2">
          <p className="text-center text-txt-secondary text-xs">{DISCLAIMER_SHORT}</p>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 text-xs font-mono">
            <Link to="/plans" className="text-terminal-cyan hover:underline py-3 px-2">Free and Pro</Link>
            <Link to="/terms" className="text-terminal-cyan hover:underline py-3 px-2">Terms</Link>
            <Link to="/privacy" className="text-terminal-cyan hover:underline py-3 px-2">Privacy</Link>
            <Link to="/methodology" className="text-terminal-cyan hover:underline py-3 px-2">How it works</Link>
            <Link to="/support" className="text-terminal-cyan hover:underline py-3 px-2">Support</Link>
          </div>
          <p className="text-center text-txt-secondary text-xs font-mono">
            ULTRONIC TERMINAL &copy; {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
